import React, { useEffect, useState } from "react";
import { useData } from "@pure-ptr/react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    FormControlLabel,
    Switch,
    IconButton,
    Typography,
    TextField,
    Button,
} from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { Loadable } from "../../../../common";
import CloseIcon from "@mui/icons-material/Close";
import { Formik } from "formik";
import { CIDR_REGEX } from "../../../../store/constants/pages";
import {
    createIpMask,
    updatePartnerMask,
} from "../../../../store/api/partnerIpmask";
import { errorToastify } from "../../../../util/toast";

const useStyles = makeStyles((theme) => ({
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    formControl: {
        width: "100%",
    },
}));

export default function PartnerIPMaskDialog({ open, handleClose, cidr ,data:currentIpCidrs}) {
    const classes = useStyles();
    const mode = cidr ? "edit" : "create";
    const savedCidr = cidr;
    const [ipMaskPayload, setIpMaskPayload] = useState({});

    const {
        data,
        isPending: isAddingIpMask,
        error,
    } = useData(async () => {
        if (Object.keys(ipMaskPayload).length === 0) return;
        const ipMaskCreateResponse = await (mode === "edit"
            ? updatePartnerMask(ipMaskPayload)
            : createIpMask(ipMaskPayload));
        handleClose(true);
        setIpMaskPayload({});

        return ipMaskCreateResponse;
    }, [ipMaskPayload]);

       useEffect(() => {
            if (!error) return;
    
            errorToastify(error?.payload?.error);
        }, [error]);
    

    const initialValues = {
        id: mode === "edit" ? savedCidr.id : undefined,
        partnerName: mode === "edit" ? savedCidr.partnerName : "",
        description: mode === "edit" ? savedCidr.description : "",
        active: mode === "edit" ? savedCidr.active : false,
        ipMask: mode === "edit" ? savedCidr.ipMask : "",
    };

    return (
        <Dialog
            fullWidth
            open={open}
            onClose={() => handleClose(false)}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">
                <Grid container>
                    <Grid item xs={6}>
                        <Typography variant="h6">
                            {mode === "edit" ? "Edit" : "Add"} Partner IP Mask
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <IconButton
                            aria-label="close"
                            className={classes.closeButton}
                            onClick={() => handleClose(false)}
                            size="large"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <Loadable loading={isAddingIpMask != null}>
                    <Grid>
                        <Formik
                            enableReinitialize={true}
                            initialValues={initialValues}
                            validate={(values) => {
                                const errors: any = {};

                                const isDuplicateIpName=currentIpCidrs?.find((item)=>item?.partnerName?.toLowerCase()===values?.partnerName?.toLowerCase()?.trim() && item?.id!==values?.id) || null

                                const charactersLimitationMsg =
                                    "Only 250 Characters";

                                if (!values.partnerName) {
                                    errors.partnerName = "Required field";
                                }
                                if (isDuplicateIpName) {
                                    errors.partnerName =
                                        "Ip Mask with this Partner Name Already Exists";
                                }

                                if (values.partnerName.length > 250) {
                                    errors.partnerName =
                                        charactersLimitationMsg;
                                }

                                if (
                                    !values.ipMask ||
                                    !CIDR_REGEX.test(values.ipMask)
                                ) {
                                    errors.ipMask = !values.ipMask
                                        ? "Required field"
                                        : "IP must be in comma-separated CIDR format, e.g., 192.168.1.0/24,10.8.0.100/32";
                                }
                                 else {
                                    const ipList = values.ipMask
                                        .split(",")
                                        .map((ip: string) => ip.trim())
                                        .filter((ip: string) => ip.length > 0);

                                    const uniqueIps = new Set(ipList);

                                    if (uniqueIps.size !== ipList.length) {
                                        errors.ipMask =
                                            "Duplicate IPs not allowed";
                                    }
                                }

                                if (values.description.length > 250) {
                                    errors.description =
                                        charactersLimitationMsg;
                                }

                                return errors;
                            }}
                            onSubmit={(
                                values,
                                { setStatus, setSubmitting }
                            ) => {
                                let shouldReloadGrid = true;
                                let payload: any = {
                                    active: values.active,
                                    ipMask: values.ipMask,
                                    partnerName: values.partnerName?.trim(),
                                    description: values.description,
                                };
                                if (mode === "create") {
                                    setIpMaskPayload({ ...payload });
                                } else {
                                    payload = {
                                        ...payload,
                                        id: values.id,
                                    };

                                    setIpMaskPayload({ ...payload });
                                }
                            }}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                setFieldValue,
                                isSubmitting,
                            }) => (
                                <form
                                    onSubmit={handleSubmit}
                                    noValidate
                                    autoComplete="off"
                                >
                                    <div>
                                        <Grid container>
                                            <Grid item xs={12} md={12}>
                                                <TextField
                                                    required
                                                    label="Partner Name"
                                                    name="partnerName"
                                                    margin="normal"
                                                    value={values.partnerName}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    error={Boolean(
                                                        touched.partnerName &&
                                                            errors.partnerName
                                                    )}
                                                    helperText={
                                                        touched.partnerName &&
                                                        errors.partnerName
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            onChange={(e) =>
                                                                setFieldValue(
                                                                    "active",
                                                                    e.target
                                                                        .checked
                                                                )
                                                            }
                                                            checked={
                                                                values.active
                                                            }
                                                        />
                                                    }
                                                    label="Active"
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={12}>
                                                <TextField
                                                    multiline
                                                    rows={3}
                                                    // maxRows={3}
                                                    label="Allowed IP/Mask"
                                                    margin="normal"
                                                    name={`ipMask`}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.ipMask || ""}
                                                    helperText={
                                                        (touched.ipMask &&
                                                            errors.ipMask) ||
                                                        "IP must be in comma-separated CIDR format, e.g., 192.168.1.0/24,10.8.0.100/32"
                                                    }
                                                    error={Boolean(
                                                        touched.ipMask &&
                                                            errors.ipMask
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={12}>
                                                <TextField
                                                    minRows={3}
                                                    multiline
                                                    label="Description"
                                                    name="description"
                                                    margin="normal"
                                                    value={values.description}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    error={Boolean(
                                                        touched.description &&
                                                            errors.description
                                                    )}
                                                    helperText={
                                                        touched.description &&
                                                        errors.description
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        <hr />
                                        <Grid
                                            container
                                            justifyContent="flex-end"
                                        >
                                            <Button
                                                onClick={() =>
                                                    handleClose(false)
                                                }
                                                variant="contained"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={` ml-3`}
                                                type="submit"
                                            >
                                                {false || false
                                                    ? "Saving Changes"
                                                    : "Save"}
                                            </Button>
                                        </Grid>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </Grid>
                </Loadable>
            </DialogContent>
        </Dialog>
    );
}
